import React, { useState } from "react";
import {
  Wallet,
  TrendUp,
  TrendDown,
  Coins,
  CreditCard,
  PiggyBank,
  ChartLine,
  Bank,
  House,
  Car,
  Users,
  Lock,
} from "@phosphor-icons/react";
import { formatCurrency } from "../utils/calculations";
import { Asset, Debt, Partner } from "../types";

interface BalanceCardProps {
  currentBalance: number;
  assets: Asset[];
  debts: Debt[];
  partners?: Partner[];
}

const BalanceCard: React.FC<BalanceCardProps> = ({
  currentBalance,
  assets,
  debts,
  partners = [],
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const totalAssets = assets.reduce((sum, asset) => sum + asset.balance, 0);
  const liquidAssets = assets
    .filter((a) => a.isLiquid)
    .reduce((sum, asset) => sum + asset.balance, 0);
  const illiquidAssets = assets
    .filter((a) => !a.isLiquid)
    .reduce((sum, asset) => sum + asset.balance, 0);
  const totalDebts = debts.reduce((sum, debt) => sum + debt.balance, 0);
  const netWorth = totalAssets - totalDebts;

  const getBalanceColor = (balance: number) => {
    if (balance >= 100000) return "text-green-500";
    if (balance >= 50000) return "text-blue-500";
    if (balance >= 10000) return "text-yellow-500";
    if (balance >= 0) return "text-orange-500";
    return "text-red-500";
  };

  const getBalanceMessage = (balance: number) => {
    if (balance >= 100000) return "Excellent balance";
    if (balance >= 50000) return "Strong balance";
    if (balance >= 10000) return "Good balance";
    if (balance >= 0) return "Moderate balance";
    return "Negative balance";
  };

  const getAssetBreakdown = () => {
    const breakdown: Record<string, number> = {};

    assets.forEach((asset) => {
      if (!breakdown[asset.type]) {
        breakdown[asset.type] = 0;
      }
      breakdown[asset.type] += asset.balance;
    });

    return breakdown;
  };

  const getDebtBreakdown = () => {
    const breakdown: Record<string, number> = {};

    debts.forEach((debt) => {
      if (!breakdown[debt.type]) {
        breakdown[debt.type] = 0;
      }
      breakdown[debt.type] += debt.balance;
    });

    return breakdown;
  };

  const assetBreakdown = getAssetBreakdown();
  const debtBreakdown = getDebtBreakdown();

  // Check if an asset is from a partner
  const isPartnerAsset = (assetId: string) => {
    return assetId.startsWith("partner-liquid-");
  };

  const getAssetTypeLabel = (type: string) => {
    switch (type) {
      case "savings":
        return "Savings";
      case "checking":
        return "Checking";
      case "investment":
        return "Investment";
      case "retirement":
        return "Retirement";
      case "ira":
        return "IRA";
      case "portfolio":
        return "Portfolio";
      case "crypto":
        return "Cryptocurrency";
      case "real-estate":
        return "Real Estate";
      case "vehicle":
        return "Vehicle";
      default:
        return "Other";
    }
  };

  const getDebtTypeLabel = (type: string) => {
    switch (type) {
      case "credit-card":
        return "Credit Card";
      case "mortgage":
        return "Mortgage";
      case "student-loan":
        return "Student Loan";
      case "car-loan":
        return "Car Loan";
      case "personal-loan":
        return "Personal Loan";
      default:
        return "Other Debt";
    }
  };

  const getAssetIcon = (type: string) => {
    switch (type) {
      case "savings":
        return (
          <PiggyBank className="w-4 h-4 text-green-500" weight="duotone" />
        );
      case "checking":
        return <Bank className="w-4 h-4 text-blue-500" weight="duotone" />;
      case "investment":
        return (
          <ChartLine className="w-4 h-4 text-yellow-500" weight="duotone" />
        );
      case "retirement":
        return <Coins className="w-4 h-4 text-purple-500" weight="duotone" />;
      case "ira":
        return <Coins className="w-4 h-4 text-purple-500" weight="duotone" />;
      case "portfolio":
        return (
          <ChartLine className="w-4 h-4 text-yellow-500" weight="duotone" />
        );
      case "crypto":
        return <TrendUp className="w-4 h-4 text-orange-500" weight="duotone" />;
      case "real-estate":
        return <House className="w-4 h-4 text-indigo-500" weight="duotone" />;
      case "vehicle":
        return <Car className="w-4 h-4 text-red-500" weight="duotone" />;
      default:
        return <Wallet className="w-4 h-4 text-gray-500" weight="duotone" />;
    }
  };

  const getDebtIcon = (type: string) => {
    switch (type) {
      case "credit-card":
        return <CreditCard className="w-4 h-4 text-red-500" weight="duotone" />;
      case "mortgage":
        return (
          <TrendDown className="w-4 h-4 text-purple-500" weight="duotone" />
        );
      case "student-loan":
        return <TrendDown className="w-4 h-4 text-blue-500" weight="duotone" />;
      case "car-loan":
        return (
          <TrendDown className="w-4 h-4 text-orange-500" weight="duotone" />
        );
      default:
        return <TrendDown className="w-4 h-4 text-gray-500" weight="duotone" />;
    }
  };

  return (
    <div className="card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-2 sm:space-y-0">
        <div className="flex items-center space-x-2">
          <Wallet className="w-6 h-6 text-green-500" weight="duotone" />
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Current Balance
          </h3>
        </div>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className={`text-xs px-3 py-2 rounded transition-colors w-full sm:w-auto ${
            showDetails
              ? "bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-600/50"
              : "bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
          }`}>
          {showDetails ? "Hide Details" : "Show Details"}
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <p
            className={`text-2xl font-bold ${getBalanceColor(currentBalance)}`}>
            {formatCurrency(currentBalance)}
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
            {getBalanceMessage(currentBalance)}
          </p>
        </div>

        {showDetails && (
          <>
            {/* Net Worth */}
            <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <TrendUp
                    className="w-4 h-4 text-green-500"
                    weight="duotone"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Net Worth
                  </span>
                </div>
                <span
                  className={`text-sm font-medium ${
                    netWorth >= 0 ? "text-green-600" : "text-red-600"
                  }`}>
                  {formatCurrency(netWorth)}
                </span>
              </div>
            </div>

            {/* Asset Breakdown */}
            {assets.length > 0 && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/30 rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-2">
                  <Coins className="w-4 h-4 text-green-500" weight="duotone" />
                  <span className="text-sm font-medium text-green-700 dark:text-green-400">
                    Assets ({assets.length})
                  </span>
                </div>
                <div className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                  {assets.map((asset) => {
                    const isPartner = isPartnerAsset(asset.id);

                    return (
                      <div
                        key={asset.id}
                        className="flex items-center justify-between gap-2">
                        <div className="flex items-center space-x-1 min-w-0 flex-1">
                          {getAssetIcon(asset.type)}
                          <span className="truncate">{asset.name}</span>
                          {isPartner && (
                            <Users
                              className="w-3 h-3 text-blue-500"
                              weight="duotone"
                            />
                          )}
                          {!asset.isLiquid && (
                            <Lock
                              className="w-3 h-3 text-gray-600"
                              weight="duotone"
                            />
                          )}
                        </div>
                        <span className="text-right flex-shrink-0 text-xs">
                          {formatCurrency(asset.balance)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Debt Breakdown */}
            {Object.keys(debtBreakdown).length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/30 rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-2">
                  <CreditCard
                    className="w-4 h-4 text-red-500"
                    weight="duotone"
                  />
                  <span className="text-sm font-medium text-red-700 dark:text-red-400">
                    Debts ({debts.length})
                  </span>
                </div>
                <div className="text-xs text-gray-700 dark:text-gray-400 space-y-1">
                  {Object.entries(debtBreakdown).map(([type, amount]) => (
                    <div
                      key={type}
                      className="flex items-center justify-between gap-2">
                      <div className="flex items-center space-x-1 min-w-0 flex-1">
                        {getDebtIcon(type)}
                        <span className="truncate">
                          {getDebtTypeLabel(type)}
                        </span>
                      </div>
                      <span className="text-right flex-shrink-0 text-xs">
                        {formatCurrency(amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Asset Composition */}
            <div className="text-xs text-gray-700 dark:text-gray-400 space-y-1">
              <p>• Liquid assets: {formatCurrency(liquidAssets)}</p>
              <p>• Illiquid assets: {formatCurrency(illiquidAssets)}</p>
              <p>• Asset count: {assets.length}</p>
              <p>• Debt count: {debts.length}</p>
              {liquidAssets > 0 && (
                <p>
                  • Liquidity ratio:{" "}
                  {((liquidAssets / totalAssets) * 100).toFixed(1)}%
                </p>
              )}
            </div>
          </>
        )}

        {!showDetails && (
          <div className="text-xs text-gray-700 dark:text-gray-400">
            <p>Net worth: {formatCurrency(netWorth)}</p>
            <p>
              {assets.length} assets, {debts.length} debts
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BalanceCard;
